import { Box, Collapse } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import { type FC, type MouseEvent, memo, useEffect, useMemo, useState } from "react";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useRecommendationsDrawerContext } from "../../../contexts/RecommendationsDrawerContext";
import { useResolvedActualDay, useWeekPunchSummary } from "../../../contexts/WeekView";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { SURFACE } from "../../../theme/tokens";
import type { StaffingIssue } from "../../../utils/autoStaffing";
import { getStaffingDate } from "../../../utils/dateUtils";
import {
	type DayItem,
	type HoursSummaryEmployeeDayShift,
	type NurseInShift,
	type OpenShiftRequest,
	type PunchSummaryCategory,
	RecommendationOpenSource,
	type ReplaceShiftRequest,
} from "../../../utils/models";
import { NormalizedRequestApprovalDialog } from "../../approvalDialogs/RequestsApproval/NormalizedRequestApprovalDialog";
import { useRecommendations } from "../../Recommendations/hooks/useRecommendations";
import { StaffingChanges, type TabType } from "../../StaffingChanges/StaffingChanges";
import { SystemReactTinyPopover } from "../../UIToolkit/Popups/SystemReactTinyPopover";
import { useDayViewItem } from "../hooks/useDayViewItem";
import { PunchActionCenter } from "../WeekSchedule/PunchActionCenter/PunchActionCenter";
import {
	ACTION_CENTER_ANCHOR_GAP_PX,
	ACTION_CENTER_VIEWPORT_INSET_PX,
	actionCenterPopoverRootStyle,
	actionCenterRowAlignOffset,
} from "../WeekSchedule/PunchActionCenter/punchActionCenterLayout";
import { DayControl } from "./DayControl/DayControl";
import { DayHeader } from "./DayHeader/DayHeader";
import { DayIssues } from "./DayIssues/DayIssues";
import { DayQuickActions } from "./DayPostShift/DayQuickActions";
import { DayRecommendations } from "./DayRecommendations/DayRecommendations";
import { DayRequests } from "./DayRequests/DayRequests";
import { FutureLookingWeeklyOvertimeDayRecord } from "./FutureLookingWeeklyOvertimeDayRecord/FutureLookingWeeklyOvertimeDayRecord";
import { PunchSummaryFooter } from "./PunchSummaryFooter/PunchSummaryFooter";

interface IProps {
	dayItem: DayItem;
	weekIndex: number;
	searchText: string | null;
	recommendations: StaffingIssue[];
	onDayClicked: (date: DateTime) => void;
	isCollapsed?: boolean;
	costlyStaffShifts?: NurseInShift[];
	shiftHoursById?: Map<string, HoursSummaryEmployeeDayShift>;
	actualShiftIds?: Set<string>;
	onCostlyShiftDoubleClick?: (shift: NurseInShift) => void;
	onActiveCategoryChange?: (date: DateTime, category: PunchSummaryCategory) => void;
	activeCategory?: PunchSummaryCategory | null;
	onActionCenterClose?: () => void;
	onSetSearch?: (name: string) => void;
	/** Marks this card's Post Shift button as the Annie walkthrough anchor. Set on one card per week. */
	isPostShiftAnchor?: boolean;
}

export const DayViewItem: FC<IProps> = memo(
	({
		dayItem,
		weekIndex,
		onDayClicked,
		searchText,
		recommendations,
		isCollapsed,
		costlyStaffShifts,
		shiftHoursById,
		actualShiftIds,
		onCostlyShiftDoubleClick,
		onActiveCategoryChange,
		activeCategory,
		onActionCenterClose,
		onSetSearch,
		isPostShiftAnchor,
	}) => {
		const { hospitalData, departmentNurseTypes, chosenDepartment, getWeekIndexByDate } = useHospitalData();
		const { track } = useAnalytics();
		const { toggleDrawerOpen } = useRecommendationsDrawerContext();
		const punchSummary = useWeekPunchSummary(dayItem.date);
		const { staffingChangesTab: recommendationsStaffingChangesTab, selectedRequest: recommendationsSelectedRequest, onApprove, onClose } = useRecommendations();
		const isBlockRecommendationsV2Enabled = useFeatureFlag(FEATURE_FLAGS.BlockRecommendationsV2);

		const { dayRecommendations, dayIssues } = useDayViewItem({ dayItem, recommendations });
		const [dateToPostShift, setDateToPostShift] = useState<DateTime>(dayItem.date);

		const [showRequests, setShowRequests] = useState<boolean>(false);
		const [staffingChangesTab, setStaffingChangesTab] = useState<TabType>();
		const [selectedRequest, setSelectedRequest] = useState<OpenShiftRequest | ReplaceShiftRequest | undefined>();
		const [isRecommendationsExpanded, setIsRecommendationsExpanded] = useState<boolean>(false);
		const [addChargeOnly, setAddChargeOnly] = useState<boolean>(false);

		const activeRequest = useMemo<OpenShiftRequest | ReplaceShiftRequest | undefined>(() => {
			return selectedRequest || recommendationsSelectedRequest;
		}, [selectedRequest, recommendationsSelectedRequest]);

		const activeStaffingChangesTab = useMemo<TabType | undefined>(() => {
			return staffingChangesTab || recommendationsStaffingChangesTab;
		}, [staffingChangesTab, recommendationsStaffingChangesTab]);

		const isPastActualDay = useMemo<boolean>(
			() => dayItem.date < getStaffingDate(DateTime.now(), chosenDepartment.dayStart),
			[dayItem.date, chosenDepartment.dayStart],
		);

		// Resolved list, not raw `shiftsActual`: a day worked only by staff who don't clock in has no
		// punch rows, but the swapped-in shifts still give the summary something to show.
		const resolvedActualDay = useResolvedActualDay(dayItem.date);
		const isActualLoaded = useMemo<boolean>(
			() => dayItem.date < DateTime.now().startOf("day") && !!resolvedActualDay.actualShifts.length,
			[dayItem.date, resolvedActualDay.actualShifts.length],
		);

		// The Punch Summary row the panel was opened from — the popover is anchored to the day card (the
		// grid's flex item), so this is what pulls the panel back down level with the row itself.
		const [actionCenterAnchorRow, setActionCenterAnchorRow] = useState<HTMLElement | null>(null);

		// `activeCategory` goes null the instant a close is triggered, which would otherwise swap the
		// popover's `content` to an empty `Box` before its own exit fade (in `SystemReactTinyPopover`)
		// has a chance to play — the wrapper would fade out an already-blank box. Keeping the last
		// known category around until the exit animation completes (`onExitComplete`) lets the real
		// `PunchActionCenter` content stay mounted and visibly fade with it.
		const [lastActiveCategory, setLastActiveCategory] = useState<PunchSummaryCategory | null>(activeCategory ?? null);
		useEffect(() => {
			if (activeCategory) setLastActiveCategory(activeCategory);
		}, [activeCategory]);

		if (!hospitalData.weeks[weekIndex]) return null;
		const weekObj = hospitalData.weeks[weekIndex];
		// Post Shift and Add Staff can move the panel to a day in another loaded week; the panel follows it
		// there so its other tabs (and the past-day check on the tab strip) read that same day.
		const pickedWeekIndex = getWeekIndexByDate(dateToPostShift);
		// DEFAULT: both pickers are bounded to the loaded weeks, so -1 only if a week unloads mid-pick — stay on this card's week.
		const panelWeekIndex = pickedWeekIndex === -1 ? weekIndex : pickedWeekIndex;
		// Closing hands the card back its own day, so the next open (and the day header) don't start on the picked one.
		const closeStaffingChanges = () => {
			setStaffingChangesTab(undefined);
			setDateToPostShift(dayItem.date);
		};
		// The card's own day, not the panel's picked one: the census tooltip sits beside `dayItem`'s census value.
		const dayObj = weekObj.weeklyShifts[0]?.days[dayItem.date.weekdayISO];

		const onEditStaff = (e: MouseEvent): void => {
			track("weekly_view_add_staff");
			e.preventDefault();
			e.stopPropagation();
			setAddChargeOnly(false);
			setStaffingChangesTab("create-shift");
		};

		const onExpandRequest = (e: MouseEvent): void => {
			e.stopPropagation();
			track("week_view_expand_request");
			setShowRequests(!showRequests);
		};

		const onRequestClick = (e: MouseEvent, request: OpenShiftRequest | ReplaceShiftRequest): void => {
			track("weekly_view_open_request");
			e.stopPropagation();
			e.preventDefault();
			setSelectedRequest(request);
		};

		const onExpandRecommendations = (e: MouseEvent): void => {
			track("weekly_view_expand_recommendations");
			e.stopPropagation();
			e.preventDefault();
			setIsRecommendationsExpanded(!isRecommendationsExpanded);
		};

		const onRecommendationClick = (): void => {
			toggleDrawerOpen(true, RecommendationOpenSource.RecommendationsClick);
		};

		const isActionCenterOpen = !!activeCategory && !!onActionCenterClose;
		const displayCategory = activeCategory ?? lastActiveCategory;

		return (
			<>
				<SystemReactTinyPopover
					isOpen={isActionCenterOpen}
					onClose={onActionCenterClose}
					onExitComplete={() => setLastActiveCategory(null)}
					// Beside the column, not over it: opening downwards covered the shift-time labels of the
					// rows below — the context a manager needs to act on what the panel lists — and left the
					// panel unreachable on a short window (see `ACTION_CENTER_VIEWPORT_INSET_PX`). `bottom`/
					// `top` stay as last resorts for a window too narrow for the panel on either side.
					positions={["right", "left", "bottom", "top"]}
					align="start"
					padding={ACTION_CENTER_ANCHOR_GAP_PX}
					boundaryInset={ACTION_CENTER_VIEWPORT_INSET_PX}
					// The arrow is invisible on this popover (`arrowOpacity` defaults to 0) but its container
					// still pads the measured popover by `arrowSize` on each side — 16px that counted against
					// the boundary check and flipped a panel that had room to the right onto the left, over
					// two other day columns. `padding` above is the real gap.
					arrowSize={0}
					transformMode="relative"
					transform={({ popoverRect }) => ({ top: actionCenterRowAlignOffset(popoverRect, actionCenterAnchorRow), left: 0 })}
					containerStyle={{ zIndex: "1301" }}
					childrenWrapperStyle={{ width: "400px", minWidth: 0 }}
					contentRootStyle={actionCenterPopoverRootStyle}
					content={
						displayCategory ? (
							<PunchActionCenter
								activeDay={dayItem.date}
								activeCategory={displayCategory}
								summary={punchSummary}
								onClose={onActionCenterClose ?? (() => {})}
								onSetSearch={onSetSearch}
							/>
						) : (
							<Box />
						)
					}
				>
					<Box
						data-testid={`day-view-${dayItem.date.toISODate()}`}
						sx={(theme) => ({
							bgcolor: SURFACE,
							transition: "background-color 0.2s ease",
							border: `1px solid ${alpha(theme.palette.primary.light, 0.15)}`,
							width: "100%",
							height: "100%",
							overflow: "auto",
							position: "relative",
							display: "flex",
							flexDirection: "column",
						})}
					>
						<DayHeader onClick={() => onDayClicked(dateToPostShift)} dayItem={dayItem} />
						<DayControl dayItem={dayItem} onEditStaff={onEditStaff} dayObj={dayObj} />
						<Collapse in={!isCollapsed}>
							{!isPastActualDay &&
								(!isBlockRecommendationsV2Enabled ? (
									<DayRecommendations
										recommendations={dayRecommendations}
										onExpandRecommendations={onExpandRecommendations}
										onApprove={onApprove}
										isRecommendationsExpanded={isRecommendationsExpanded}
									/>
								) : (
									<DayIssues
										recommendations={dayIssues}
										onExpandRecommendations={onExpandRecommendations}
										onClick={onRecommendationClick}
										isRecommendationsExpanded={isRecommendationsExpanded}
									/>
								))}
							{!isPastActualDay && (
								<DayRequests dayRequests={dayItem.requests} onExpandRequest={onExpandRequest} onRequestClick={onRequestClick} showRequests={showRequests} />
							)}
							{!isPastActualDay && costlyStaffShifts !== undefined && (
								<FutureLookingWeeklyOvertimeDayRecord
									shifts={costlyStaffShifts}
									shiftHoursById={shiftHoursById}
									actualShiftIds={actualShiftIds}
									searchText={searchText}
									onShiftDoubleClick={onCostlyShiftDoubleClick}
								/>
							)}
						</Collapse>
						{/* Past days have no recommendations, requests or projections above, so their action row sits higher than on future days. */}
						{isActualLoaded ? (
							<Collapse in={!isCollapsed}>
								<PunchSummaryFooter
									dayDate={dayItem.date}
									onClick={(category, rowElement) => {
										setActionCenterAnchorRow(rowElement);
										onActiveCategoryChange?.(dayItem.date, category);
									}}
									summary={punchSummary}
								/>
							</Collapse>
						) : (
							<DayQuickActions
								isPostShiftAnchor={isPostShiftAnchor}
								onTabOpen={(tab) => {
									setAddChargeOnly(false);
									setStaffingChangesTab(tab);
								}}
							/>
						)}
					</Box>
				</SystemReactTinyPopover>
				{activeStaffingChangesTab && (
					<StaffingChanges
						setIsAdd={closeStaffingChanges}
						dayNo={dateToPostShift.weekday % 7}
						weekIndex={panelWeekIndex}
						onSelectedDateChange={setDateToPostShift}
						shiftNo={0}
						rolesToDisplay={departmentNurseTypes}
						onClose={() => {
							closeStaffingChanges();
							onClose();
						}}
						defaultTab={activeStaffingChangesTab}
						isCharged={addChargeOnly}
						// Opened on a whole day, not one shift: Add Staff asks which shift first.
						initialShiftSelected={false}
					/>
				)}

				{activeRequest && (
					<NormalizedRequestApprovalDialog
						open={!!activeRequest}
						onClose={() => {
							setSelectedRequest(undefined);
							onClose();
						}}
						request={activeRequest}
					/>
				)}
			</>
		);
	},
);
